import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
    getFirestore, 
    collection, 
    doc, 
    getDocs, 
    getDoc,
    query, 
    orderBy, 
    limit,
    where,
    Timestamp 
} from 'firebase/firestore';

const functions = getFunctions();
const db = getFirestore();

/**
 * 🚀 Firestore Site Publisher
 * 
 * Interface für das neue Firestore-basierte Publishing-System
 * Läuft parallel zum bestehenden RTDB-System
 */

/**
 * Publish site to Firestore with versioning
 */
export const publishSiteToFirestore = async (siteId, pageId, versionNote = "Auto-publish") => {
    console.log('🚀 Publishing site to Firestore:', { siteId, pageId, versionNote });
    
    try {
        // Call Cloud Function
        const publishSite = httpsCallable(functions, 'publishSite');
        const result = await publishSite({
            siteId,
            pageId,
            versionNote
        });
        
        console.log('✅ Site published successfully:', result.data);
        return result.data;
        
    } catch (error) {
        console.error('❌ Failed to publish site:', error);
        throw error;
    }
};

/**
 * Get site information from Firestore with improved error handling
 */
export const getSiteInfo = async (siteId) => {
    try {
        // Validiere siteId
        if (!siteId || typeof siteId !== 'string') {
            throw new Error('Invalid siteId provided');
        }
        
        const siteDoc = await getDoc(doc(db, 'sites', siteId));
        
        if (!siteDoc.exists()) {
            return null;
        }
        
        return {
            id: siteDoc.id,
            ...siteDoc.data()
        };
        
    } catch (error) {
        // Reduzierte Fehler-Logs für bekannte Probleme
        if (error.code === 'permission-denied') {
            console.warn('⚠️ Firestore permission denied for site:', siteId);
        } else if (error.code === 'not-found') {
            console.log('ℹ️ Site not found:', siteId);
        } else {
            console.error('❌ Error getting site info:', error.message);
        }
        throw error;
    }
};

/**
 * Get site versions with pagination
 */
export const getSiteVersions = async (siteId, limitCount = 10) => {
    try {
        const versionsQuery = query(
            collection(db, 'sites', siteId, 'versions'),
            orderBy('publishedAt', 'desc'),
            limit(limitCount)
        );
        
        const versionsSnapshot = await getDocs(versionsQuery);
        const versions = [];
        
        versionsSnapshot.forEach(doc => {
            versions.push({
                id: doc.id,
                ...doc.data(),
                publishedAt: doc.data().publishedAt?.toDate() // Convert Firestore Timestamp
            });
        });
        
        return versions;
        
    } catch (error) {
        console.error('❌ Error getting site versions:', error);
        throw error;
    }
};

/**
 * Get rendered content for a specific version
 */
export const getRenderedContent = async (siteId, versionId) => {
    try {
        const renderedDoc = await getDoc(doc(db, 'sites', siteId, 'rendered', versionId));
        
        if (!renderedDoc.exists()) {
            return null;
        }
        
        return {
            id: renderedDoc.id,
            ...renderedDoc.data(),
            generatedAt: renderedDoc.data().generatedAt?.toDate()
        };
        
    } catch (error) {
        console.error('❌ Error getting rendered content:', error);
        throw error;
    }
};

/**
 * Rollback to a previous version
 */
export const rollbackToVersion = async (siteId, targetVersionId, rollbackNote = "Rollback") => {
    console.log('🔄 Rolling back to version:', { siteId, targetVersionId });
    
    try {
        // Call Cloud Function for rollback
        const rollbackSite = httpsCallable(functions, 'rollbackSite');
        const result = await rollbackSite({
            siteId,
            targetVersionId,
            rollbackNote
        });
        
        console.log('✅ Rollback successful:', result.data);
        return result.data;
        
    } catch (error) {
        console.error('❌ Rollback failed:', error);
        throw error;
    }
};

/**
 * Get live site URL
 */
export const getLiveSiteUrl = (siteInfo) => {
    if (!siteInfo) return null;
    
    // Custom domain takes precedence (with /to/ prefix)
    if (siteInfo.customDomain && siteInfo.domainStatus === 'verified') {
        return `https://${siteInfo.customDomain}/to/${siteInfo.username}/`;
    }
    
    // Fallback to bonusclub subdomain (with /to/ prefix for consistency)
    return `https://bonusclub.link/to/${siteInfo.username}/`;
};

/**
 * Check if site is published in Firestore
 */
export const isSitePublished = async (siteId) => {
    try {
        const siteInfo = await getSiteInfo(siteId);
        return siteInfo && siteInfo.status === 'live' && siteInfo.publishedVersion;
        
    } catch (error) {
        console.error('❌ Error checking site publish status:', error);
        return false;
    }
};

/**
 * Get site analytics/stats with graceful error handling
 */
export const getSiteStats = async (siteId) => {
    try {
        // Validiere siteId
        if (!siteId || typeof siteId !== 'string') {
            return null;
        }
        
        const siteInfo = await getSiteInfo(siteId);
        
        // Wenn keine Site-Info verfügbar, return null ohne weitere Calls
        if (!siteInfo) {
            return null;
        }
        
        const versions = await getSiteVersions(siteId, 5);
        
        return {
            isPublished: siteInfo?.status === 'live',
            currentVersion: siteInfo?.publishedVersion,
            totalVersions: versions.length,
            lastPublished: siteInfo?.publishedAt?.toDate(),
            lastPublishedBy: siteInfo?.lastPublishedBy,
            liveUrl: getLiveSiteUrl(siteInfo),
            recentVersions: versions
        };
        
    } catch (error) {
        // Stille Fehlerbehandlung - keine wiederholten Console-Logs
        if (error.code !== 'permission-denied' && error.code !== 'not-found') {
            console.error('❌ Error getting site stats:', error.message);
        }
        return null;
    }
};

/**
 * Delete site and all versions
 */
export const deleteSite = async (siteId) => {
    console.log('🗑️ Deleting site:', siteId);
    
    try {
        // Call Cloud Function for safe deletion
        const deleteSiteFunction = httpsCallable(functions, 'deleteSite');
        const result = await deleteSiteFunction({ siteId });
        
        console.log('✅ Site deleted successfully:', result.data);
        return result.data;
        
    } catch (error) {
        console.error('❌ Site deletion failed:', error);
        throw error;
    }
};

/**
 * Preview a specific version (without publishing)
 */
export const getVersionPreviewUrl = (siteId, versionId) => {
    return `https://preview-${versionId}.${siteId}.bonusclub.app`;
};

/**
 * Export site data for backup
 */
export const exportSiteData = async (siteId) => {
    try {
        const siteInfo = await getSiteInfo(siteId);
        const versions = await getSiteVersions(siteId, 999); // Get all versions
        
        return {
            site: siteInfo,
            versions,
            exportedAt: new Date().toISOString(),
            exportVersion: '1.0.0'
        };
        
    } catch (error) {
        console.error('❌ Error exporting site data:', error);
        throw error;
    }
};

// Utility functions for integration
export const firestorePublisherUtils = {
    /**
     * Convert RTDB page ID to Firestore site ID
     */
    pageIdToSiteId: (pageId) => {
        // Extract spaceId from pageId (userId/spaceId/pageName)
        const parts = pageId.split('/');
        return parts.length >= 2 ? parts[1] : pageId;
    },
    
    /**
     * Generate version note from page data
     */
    generateVersionNote: (pageData, hasChanges = false) => {
        const timestamp = new Date().toLocaleString();
        
        if (hasChanges) {
            return `Deploy changes - ${timestamp}`;
        }
        
        return `Initial publish - ${timestamp}`;
    },
    
    /**
     * Format version for display
     */
    formatVersion: (version) => {
        if (!version || !version.publishedAt) return 'Unknown';
        
        const date = version.publishedAt instanceof Date ? 
            version.publishedAt : 
            version.publishedAt.toDate();
            
        return `${version.versionId} (${date.toLocaleDateString()})`;
    }
};
